import { StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patchState } from '../../nested-value-getter';
import { OrmService } from '../../schema/orm.service';
import { AddressRef } from '../../schema/schema.interface';
import { MachinesFirestoreService } from '../../services/machine.firestore.service';
import { MachineActions } from './machine.action';
import { MachineStateModel } from './machine.store';

@StateRepository()
@State<MachineStateModel>({
  name: 'machines',
  defaults: {
    machines: [],
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
  },
})
@Injectable()
export class MachineState extends NgxsDataRepository<MachineStateModel> {
  private machineService = inject(MachinesFirestoreService);
  private dbUser = inject(OrmService).userCollection;

  @Action(MachineActions.GetAllMachine)
  async getAllMachines(ctx: StateContext<MachineStateModel>) {
    ctx.patchState({ isLoading: true });
    const machines = await this.machineService.GetAll();
    ctx.patchState({ machines: machines.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }

  @Action(MachineActions.InitMachines)
  initMachines(ctx: StateContext<MachineStateModel>, { id }: MachineActions.InitMachines) {
    const state = ctx.getState();
    if (!state.isSubscribed) {
      ctx.patchState({ isSubscribed: true, isLoading: true });
      let off;
      if (id) {
        const { runs } = this.machineService.SetQuery({
          filterModel: { userId: { filter: this.dbUser.ref(this.dbUser.id(id)), type: 'equals' } },
        });
        console.log({ state, off, id, runs });
        off = this.machineService.OnQueryAll(runs, (machines) => {
          ctx.patchState({
            machines: machines
              .filter((machine): machine is NonNullable<typeof machine> => Boolean(machine))
              .map(({ data, ref }) => ({ ...data, ref })),
            isLoading: false,
          });
        });
      } else {
        off = this.machineService.OnGetAll((data) => ctx.dispatch(new MachineActions.UpdateMachineResult(data)));
      }
      off.catch((err) => ctx.dispatch(new MachineActions.MachineError(err as Error)));
      ctx.patchState({ off, isLoading: false });
    }
  }

  @Action(MachineActions.CloseMachinesConnection)
  closeMachinesConnection(ctx: StateContext<MachineStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(MachineActions.UpdateMachine)
  async updateMachine(ctx: StateContext<MachineStateModel>, { machineData, ref }: MachineActions.UpdateMachine) {
    await this.machineService.Update(ref, machineData);
  }

  @Action(MachineActions.UpdateMachineResult)
  updateMachineResult(ctx: StateContext<MachineStateModel>, { data }: MachineActions.UpdateMachineResult) {
    patchState<MachineStateModel>(ctx, 'machines', data);
  }

  @Action(MachineActions.MachineError)
  machineError(ctx: StateContext<MachineStateModel>, { error }: MachineActions.MachineError) {
    console.error({ error });
  }

  @Action(MachineActions.SetSelectedAddress)
  machinesByAddress(ctx: StateContext<MachineStateModel>, { selectedAddress }: MachineActions.SetSelectedAddress) {
    ctx.patchState({ selectedAddress });
  }

  @Action(MachineActions.DeleteMachine)
  async deleteMachines(ctx: StateContext<MachineStateModel>, { refs }: MachineActions.DeleteMachine) {
    const machines = ctx.getState().machines;
    if (!Array.isArray(refs)) refs = [refs];
    const deleteRes = await this.machineService.BatchDelete(refs);
    if (deleteRes) {
      const deletedIds = deleteRes.map((x) => x.id);

      ctx.patchState({ machines: machines.filter((x) => !deletedIds.includes(x.ref.id)) });
    }
  }

  @Action(MachineActions.QueryMachine)
  async queryMachine(ctx: StateContext<MachineStateModel>, { filter }: MachineActions.QueryMachine) {
    ctx.patchState({ isLoading: true });
    const machines = await this.machineService.QueryAll(filter.query);
    ctx.patchState({ machines: machines.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }

  @Selector()
  static machines(state: MachineStateModel) {
    return state.machines;
  }

  @Selector()
  static machinesByAddress(state: MachineStateModel) {
    const addressRef: AddressRef | undefined = state.selectedAddress?.ref;
    if (addressRef) {
      return state.machines.filter(
        (machine) =>
          machine.addressId?.id === addressRef?.id &&
          machine.addressId?.collection?.path === addressRef?.collection?.path,
      );
    }
    return undefined;
  }

  @Selector()
  static machineState(state: MachineStateModel) {
    return state;
  }
}
